import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Star } from "@phosphor-icons/react";

/**
 * Renders a dynamic form from an assessment template's field list.
 * value: dict of key -> value.
 * onChange(nextValue)
 */
export default function AssessmentTemplateForm({ template, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  if (!template) return null;

  return (
    <div className="space-y-3">
      {(template.fields || []).map((f) => (
        <div key={f.key} className="space-y-1.5">
          <Label>{f.label}{f.required && " *"}</Label>
          {f.type === "rating" && <RatingField v={value[f.key]} onChange={(x) => set(f.key, x)} testid={`tpl-${f.key}`} />}
          {f.type === "number" && <Input type="number" value={value[f.key] ?? ""} onChange={e => set(f.key, e.target.value === "" ? "" : Number(e.target.value))} data-testid={`tpl-${f.key}`} />}
          {f.type === "text" && <Input value={value[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} data-testid={`tpl-${f.key}`} />}
          {f.type === "textarea" && <Textarea rows={2} value={value[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} data-testid={`tpl-${f.key}`} />}
          {f.type === "select" && (
            <Select value={value[f.key] ?? ""} onValueChange={x => set(f.key, x)}>
              <SelectTrigger data-testid={`tpl-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-card">
                {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {f.type === "checklist" && (
            <div className="flex flex-wrap gap-2">
              {(f.options || []).map((o) => {
                const arr = Array.isArray(value[f.key]) ? value[f.key] : [];
                const on = arr.includes(o);
                return (
                  <label key={o} className={`flex items-center gap-1.5 border rounded-md px-2.5 py-1.5 cursor-pointer text-sm ${on ? "border-primary bg-primary/5" : "border-border"}`}>
                    <Checkbox checked={on} onCheckedChange={() => set(f.key, on ? arr.filter(x => x !== o) : [...arr, o])} />
                    {o}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RatingField({ v, onChange, testid }) {
  const val = Number(v) || 0;
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}>
          <Star size={24} weight={n <= val ? "fill" : "regular"} className={n <= val ? "text-amber-500" : "text-muted-foreground"} />
        </button>
      ))}
      {val > 0 && <span className="text-xs text-muted-foreground ml-2 self-center">{val}/5</span>}
    </div>
  );
}
